import { getAccessToken } from "../utils/auth";
import React, { useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import axios from "../utils/api";
import { useNavigate } from "react-router-dom";
import { ADMIN_ROLES, isValidDateString, getRoleLabel } from "../constants/adminRoles";
import {
  QUALIFICATION_GROUPS,
  resolveQualification,
} from "../constants/adminQualifications";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const capitalizeSetting = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "—";

const previewFieldStyles = {
  item: {
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    padding: "12px 14px",
    border: "1px solid #e2e8f0",
  },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "4px",
  },
  value: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#0f172a",
    wordBreak: "break-word",
  },
};

function PreviewField({ label, value }) {
  return (
    <div style={previewFieldStyles.item}>
      <div style={previewFieldStyles.label}>{label}</div>
      <div style={previewFieldStyles.value}>{value || "—"}</div>
    </div>
  );
}

function validateAdminFormFields({
  name,
  employeeId,
  email,
  phoneNumber,
  dob,
  dateOfJoin,
  qualification,
  role,
  password,
}) {
  const errors = {};

  if (!name.trim()) {
    errors.name = "Name is required";
  } else if (!name.match(/^[a-zA-Z\s]+$/)) {
    errors.name = "Name can only contain letters and spaces";
  }

  if (!employeeId.trim()) {
    errors.employeeId = "Employee ID is required";
  }

  if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    errors.email = "Invalid email format";
  }

  if (!phoneNumber.match(/^\d{10}$/)) {
    errors.phoneNumber = "Valid phone number is required (10 digits)";
  }

  if (!dob) {
    errors.dob = "Date of birth is required";
  } else if (!isValidDateString(dob)) {
    errors.dob = "Date of birth must be in YYYY-MM-DD format";
  }

  if (!dateOfJoin) {
    errors.dateOfJoin = "Date of join is required";
  } else if (!isValidDateString(dateOfJoin)) {
    errors.dateOfJoin = "Date of join must be in YYYY-MM-DD format";
  }

  if (!qualification.trim()) {
    errors.qualification =
      "Select a qualification from the list or enter one manually";
  } else if (qualification.trim().length > 200) {
    errors.qualification = "Qualification must be 200 characters or fewer";
  }

  if (!role || !ADMIN_ROLES.includes(role)) {
    errors.role = "Please select a valid role";
  }

  if (!password || password.length < 8) {
    errors.password = "Password must be at least 8 characters";
  }

  return errors;
}

const CreateAdmin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dob, setDob] = useState("");
  const [dateOfJoin, setDateOfJoin] = useState("");
  const [qualificationOption, setQualificationOption] = useState("");
  const [qualificationManual, setQualificationManual] = useState("");
  const [isManualQualification, setIsManualQualification] = useState(false);
  const [manualMapping, setManualMapping] = useState("enabled");
  const [objectDisinfection, setObjectDisinfection] = useState("enabled");
  const [role, setRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [errors, setErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const qualification = useMemo(
    () =>
      resolveQualification(
        qualificationOption,
        qualificationManual,
        isManualQualification
      ),
    [qualificationOption, qualificationManual, isManualQualification]
  );

  const formValues = useMemo(
    () => ({
      name,
      employeeId,
      email,
      phoneNumber,
      dob,
      dateOfJoin,
      qualification,
      role,
      password,
    }),
    [
      name,
      employeeId,
      email,
      phoneNumber,
      dob,
      dateOfJoin,
      qualification,
      role,
      password,
    ]
  );

  const validateFields = () => validateAdminFormFields(formValues);

  const isFormComplete = useMemo(
    () => Object.keys(validateAdminFormFields(formValues)).length === 0,
    [formValues]
  );

  const hasAllFieldsFilled = useMemo(
    () =>
      Boolean(
        formValues.name.trim() &&
          formValues.employeeId.trim() &&
          formValues.email.trim() &&
          formValues.phoneNumber.length === 10 &&
          formValues.dob &&
          formValues.dateOfJoin &&
          formValues.qualification.trim() &&
          formValues.role &&
          formValues.password.length >= 8
      ),
    [formValues]
  );

  const handleRegister = async () => {
    const validationErrors = validateFields();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setPreviewOpen(false);
      return;
    }

    const token = getAccessToken();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        "/register-admin",
        {
          email,
          password,
          name,
          employeeId,
          phoneNumber,
          dob,
          dateOfJoin,
          qualification: qualification.trim(),
          manualMapping,
          objectDisinfection,
          role,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSnackbarMessage(response.data.message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setEmail("");
      setPassword("");
      setName("");
      setEmployeeId("");
      setPhoneNumber("");
      setDob("");
      setDateOfJoin("");
      setQualificationOption("");
      setQualificationManual("");
      setIsManualQualification(false);
      setManualMapping("enabled");
      setObjectDisinfection("enabled");
      setRole("");
      setErrors({});
      setPreviewOpen(false);
      setTimeout(() => {
        navigate("/admin-user");
      }, 3000);
    } catch (error) {
      setSnackbarMessage(
        error.response?.data?.message || "An error occurred. Please try again."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await handleRegister();
  };

  const handleViewPreview = () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setPreviewOpen(true);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleQualificationOptionChange = (e) => {
    const value = e.target.value;
    setIsManualQualification(false);
    setQualificationOption(value);
    setQualificationManual("");
  };

  const handleManualQualificationSelect = () => {
    setIsManualQualification(true);
    setQualificationOption("");
  };

  const renderPreviewContent = () => (
    <>
      <p style={styles.previewSubtitle}>
        Confirm the information below before registering this admin.
      </p>

      <div style={styles.previewSection}>
        <div style={styles.previewSectionTitle}>Profile</div>
        <div style={styles.previewGrid}>
          <PreviewField label="Full Name" value={name} />
          <PreviewField label="Employee ID" value={employeeId} />
          <PreviewField label="Email" value={email} />
          <PreviewField label="Phone Number" value={phoneNumber} />
          <PreviewField label="Date of Birth" value={formatDisplayDate(dob)} />
          <PreviewField label="Date of Join" value={formatDisplayDate(dateOfJoin)} />
          <PreviewField label="Qualification" value={qualification.trim()} />
          <PreviewField label="Role" value={getRoleLabel(role)} />
        </div>
      </div>

      <div style={styles.previewSection}>
        <div style={styles.previewSectionTitle}>Permissions</div>
        <div style={styles.previewGrid}>
          <PreviewField label="Manual Mapping" value={capitalizeSetting(manualMapping)} />
          <PreviewField
            label="Object Disinfection"
            value={capitalizeSetting(objectDisinfection)}
          />
        </div>
      </div>

      <div style={{ ...styles.previewSection, borderBottom: "none", marginBottom: 0 }}>
        <div style={styles.previewSectionTitle}>Account</div>
        <div style={styles.previewGrid}>
          <PreviewField
            label="Password"
            value={showPassword ? password : "•".repeat(password.length)}
          />
        </div>
      </div>
    </>
  );

  return (
    <div style={styles.container}>
      <IconButton
        onClick={() => navigate(-1)}
        style={styles.backButton}
      >
        <ArrowBackIcon />
      </IconButton>
      
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h1 style={styles.title}>Create Admin</h1>
          <p style={styles.subtitle}>Add a new administrator to the system</p>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>👤</span>
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={styles.input}
                placeholder="Enter full name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <div style={styles.error}>{errors.name}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🆔</span>
                Employee ID
              </label>
              <input
                type="text"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                style={styles.input}
                placeholder="Enter employee ID"
              />
              {errors.employeeId && <div style={styles.error}>{errors.employeeId}</div>}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📧</span>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                placeholder="Enter email address"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <div style={styles.error}>{errors.email}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📱</span>
                Phone Number
              </label>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setPhoneNumber(onlyDigits);
                }}
                style={styles.input}
                placeholder="Enter phone number"
                className={errors.phoneNumber ? 'error' : ''}
              />
              {errors.phoneNumber && <div style={styles.error}>{errors.phoneNumber}</div>}
            </div>
          </div>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🎂</span>
                Date of Birth
              </label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                style={styles.input}
                className={errors.dob ? 'error' : ''}
              />
              {errors.dob && <div style={styles.error}>{errors.dob}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📅</span>
                Date of Join *
              </label>
              <input
                type="date"
                value={dateOfJoin}
                onChange={(e) => setDateOfJoin(e.target.value)}
                style={styles.input}
                className={errors.dateOfJoin ? 'error' : ''}
              />
              {errors.dateOfJoin && <div style={styles.error}>{errors.dateOfJoin}</div>}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🎓</span>
                Qualification / Education *
              </label>
              <div style={styles.selectWrapper}>
                <select
                  value={isManualQualification ? "" : qualificationOption}
                  onChange={handleQualificationOptionChange}
                  style={styles.select}
                  className={errors.qualification ? "error" : ""}
                >
                  <option value="">Select qualification</option>
                  {QUALIFICATION_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span style={styles.selectArrow}>▼</span>
              </div>
              <button
                type="button"
                onClick={handleManualQualificationSelect}
                style={{
                  ...styles.manualQualificationButton,
                  ...(isManualQualification
                    ? styles.manualQualificationButtonActive
                    : {}),
                }}
              >
                <span style={styles.manualQualificationIcon}>✏️</span>
                <span>
                  <strong>Other — enter manually</strong>
                  <span style={styles.manualQualificationHint}>
                    {" "}
                    — use if your qualification is not in the list above
                  </span>
                </span>
              </button>
              {isManualQualification && (
                <input
                  type="text"
                  value={qualificationManual}
                  onChange={(e) =>
                    setQualificationManual(e.target.value.slice(0, 200))
                  }
                  style={{
                    ...styles.input,
                    ...styles.manualQualificationInput,
                  }}
                  placeholder="Enter your qualification"
                  className={errors.qualification ? "error" : ""}
                  autoFocus
                />
              )}
              {errors.qualification && (
                <div style={styles.error}>{errors.qualification}</div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>⚡</span>
                Role
              </label>
              <div style={styles.selectWrapper}>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  style={styles.select}
                  className={errors.role ? 'error' : ''}
                >
                  <option value="">Select Role</option>
                  {ADMIN_ROLES.map((roleValue) => (
                    <option key={roleValue} value={roleValue}>
                      {getRoleLabel(roleValue)}
                    </option>
                  ))}
                </select>
                <span style={styles.selectArrow}>▼</span>
              </div>
              {errors.role && <div style={styles.error}>{errors.role}</div>}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🗺️</span>
                Manual Mapping
              </label>
              <div style={styles.selectWrapper}>
                <select
                  value={manualMapping}
                  onChange={(e) => setManualMapping(e.target.value)}
                  style={styles.select}
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
                <span style={styles.selectArrow}>▼</span>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🧪</span>
                Object Disinfection
              </label>
              <div style={styles.selectWrapper}>
                <select
                  value={objectDisinfection}
                  onChange={(e) => setObjectDisinfection(e.target.value)}
                  style={styles.select}
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
                <span style={styles.selectArrow}>▼</span>
              </div>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>🔒</span>
              Password
            </label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.passwordInput}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={handleClickShowPassword}
                style={styles.showPasswordButton}
              >
                {showPassword ? "👁️ Hide" : "👁️ Show"}
              </button>
            </div>
            {(errors.password || (password.length > 0 && password.length < 8)) && (
              <div style={styles.error}>
                {errors.password || "Password must be at least 8 characters"}
              </div>
            )}
            <div style={styles.passwordStrength}>
              <div style={{
                ...styles.strengthBar,
                width: password.length >= 8 ? '100%' : password.length > 0 ? `${(password.length/8)*100}%` : '0%',
                backgroundColor: password.length >= 8 ? '#10b981' : password.length > 0 ? '#f59e0b' : '#e5e7eb'
              }} />
            </div>
          </div>

          <div style={styles.actionRow}>
            {hasAllFieldsFilled && (
              <button
                type="button"
                onClick={handleViewPreview}
                style={styles.previewButton}
                disabled={isSubmitting}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                View Preview
              </button>
            )}
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...(hasAllFieldsFilled ? { flex: 1 } : {}),
              }}
              disabled={isSubmitting}
            >
              <span style={styles.submitIcon}>{isSubmitting ? "⏳" : "✨"}</span>
              {isSubmitting ? "Registering..." : "Register Admin"}
            </button>
          </div>
        </form>
      </div>

      <Dialog
        open={previewOpen}
        onClose={() => !isSubmitting && setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 24px 80px rgba(99, 102, 241, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.35rem",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pb: 0.5,
          }}
        >
          Review Admin Details
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {renderPreviewContent()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setPreviewOpen(false)}
            disabled={isSubmitting}
            startIcon={<EditOutlinedIcon />}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px" }}
          >
            Back to Edit
          </Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={isSubmitting || !isFormComplete}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
            }}
          >
            {isSubmitting ? "Registering..." : "Register Admin"}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbarOpen && (
        <div
          style={{
            ...styles.snackbar,
            backgroundColor: snackbarSeverity === "success" ? "#10b981" : "#ef4444",
          }}
        >
          <span style={styles.snackbarIcon}>
            {snackbarSeverity === "success" ? "✅" : "❌"}
          </span>
          {snackbarMessage}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    padding: "20px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  backButton: {
    position: "absolute",
    top: "24px",
    left: "24px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 1)",
      transform: "scale(1.05)",
      boxShadow: "0 6px 25px rgba(0, 0, 0, 0.15)",
    },
  },
  card: {
    backgroundColor: "transparent",
    backdropFilter: "none",
    borderRadius: "0px",
    boxShadow: "none",
    padding: "0px",
    width: "100%",
    maxWidth: "1000px",
    animation: "none",
    border: "none",
  },
  cardHeader: {
    textAlign: "center",
    marginBottom: "35px",
  },
  title: {
    color: "#1a1a1a",
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "8px",
    letterSpacing: "-0.5px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  subtitle: {
    color: "#666",
    fontSize: "16px",
    fontWeight: "400",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    "@media (max-width: 640px)": {
      gridTemplateColumns: "1fr",
    },
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    animation: "fadeIn 0.5s ease-out",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  labelIcon: {
    fontSize: "18px",
  },
  input: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    fontSize: "15px",
    transition: "all 0.3s ease",
    backgroundColor: "white",
    "&:focus": {
      outline: "none",
      borderColor: "#667eea",
      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
    },
    "&.error": {
      borderColor: "#ef4444",
    },
    "&:hover": {
      borderColor: "#9ca3af",
    },
  },
  selectWrapper: {
    position: "relative",
  },
  select: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    fontSize: "15px",
    backgroundColor: "white",
    appearance: "none",
    width: "100%",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#667eea",
      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
    },
    "&:hover": {
      borderColor: "#9ca3af",
    },
  },
  selectArrow: {
    position: "absolute",
    right: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
    fontSize: "12px",
  },
  manualQualificationButton: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    width: "100%",
    marginTop: "10px",
    padding: "12px 14px",
    textAlign: "left",
    borderRadius: "12px",
    border: "2px dashed #f59e0b",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    fontSize: "14px",
    lineHeight: 1.45,
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.12)",
  },
  manualQualificationButtonActive: {
    border: "2px solid #f59e0b",
    backgroundColor: "#fef3c7",
    boxShadow: "0 4px 14px rgba(245, 158, 11, 0.22)",
  },
  manualQualificationIcon: {
    fontSize: "18px",
    flexShrink: 0,
    marginTop: "1px",
  },
  manualQualificationHint: {
    fontWeight: "400",
    color: "#b45309",
    fontSize: "13px",
  },
  manualQualificationInput: {
    marginTop: "10px",
    borderColor: "#f59e0b",
    backgroundColor: "#fffbeb",
  },
  passwordContainer: {
    display: "flex",
    gap: "10px",
  },
  passwordInput: {
    flex: 1,
    padding: "12px 16px",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    fontSize: "15px",
    transition: "all 0.3s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#667eea",
      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
    },
  },
  showPasswordButton: {
    padding: "0 20px",
    backgroundColor: "#f3f4f6",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    color: "#4b5563",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#e5e7eb",
      borderColor: "#9ca3af",
    },
  },
  passwordStrength: {
    height: "4px",
    backgroundColor: "#e5e7eb",
    borderRadius: "2px",
    marginTop: "4px",
    overflow: "hidden",
  },
  strengthBar: {
    height: "100%",
    transition: "all 0.3s ease",
    borderRadius: "2px",
  },
  error: {
    color: "#ef4444",
    fontSize: "12px",
    fontWeight: "500",
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "&::before": {
      content: "'⚠️'",
      fontSize: "12px",
    },
  },
  actionRow: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
    flexWrap: "wrap",
  },
  previewButton: {
    padding: "16px 24px",
    backgroundColor: "#fff",
    color: "#4f46e5",
    border: "2px solid #c7d2fe",
    borderRadius: "14px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    flex: 1,
    minWidth: "180px",
  },
  previewCard: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 40px rgba(102, 126, 234, 0.12)",
    padding: "28px",
    animation: "fadeIn 0.4s ease-out",
  },
  previewTitle: {
    margin: "0 0 6px",
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a1a1a",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  previewSubtitle: {
    margin: "0 0 24px",
    color: "#6b7280",
    fontSize: "15px",
  },
  previewSection: {
    marginBottom: "22px",
    paddingBottom: "18px",
    borderBottom: "1px solid #f1f5f9",
  },
  previewSectionTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#6366f1",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: "12px",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  previewItem: {
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    padding: "12px 14px",
    border: "1px solid #e2e8f0",
  },
  previewLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "4px",
  },
  previewValue: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#0f172a",
    wordBreak: "break-word",
  },
  submitButton: {
    padding: "16px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "14px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
    flex: 1,
    minWidth: "180px",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(102, 126, 234, 0.5)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },
  submitIcon: {
    fontSize: "18px",
  },
  snackbar: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "white",
    padding: "16px 24px",
    borderRadius: "14px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
    animation: "slideIn 0.3s ease-out",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    fontWeight: "500",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    zIndex: 1000,
  },
  snackbarIcon: {
    fontSize: "20px",
  },
};

// Add keyframes for animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 640px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }
`;
document.head.appendChild(styleSheet);

export default CreateAdmin;